/**
 * DEMO pool: constant-product reserves equivalent to a single full-range v4 position, with the same fee
 * semantics as RunnerHook (project fee on the quote side; gross for buys, quote output for sells) plus the LP fee.
 * Simulation only — the live app quotes the real PoolManager through the V4Quoter.
 */
export const PIPS = 1_000_000n;

export interface PoolState {
  runner: bigint; // virtual RUNNER reserve
  quote: bigint; // virtual quote reserve
  lpFeePips: bigint;
  hookFeeBps: bigint;
}

export interface SwapResult {
  amountIn: bigint;
  amountOut: bigint;
  projectFee: bigint;
  lpFee: bigint;
  runnerDelta: bigint; // + for buys (RUNNER to buyer), - for sells
  priceImpactBps: number;
}

export function price(p: PoolState): number {
  return Number(p.quote) / Number(p.runner);
}

/** Exact-input buy: `quoteIn` gross includes the project fee. */
export function quoteBuy(p: PoolState, quoteIn: bigint): SwapResult {
  const projectFee = (quoteIn * p.hookFeeBps) / 10_000n;
  const toPool = quoteIn - projectFee;
  const lpFee = (toPool * p.lpFeePips) / PIPS;
  const eff = toPool - lpFee;
  const out = (p.runner * eff) / (p.quote + eff);
  const mid = Number(eff) / price(p);
  const impact = mid > 0 ? Math.max(0, Math.round((1 - Number(out) / mid) * 10_000)) : 0;
  return { amountIn: quoteIn, amountOut: out, projectFee, lpFee, runnerDelta: out, priceImpactBps: impact };
}

/** Exact-input sell: the project fee is taken from the quote output. */
export function quoteSell(p: PoolState, runnerIn: bigint): SwapResult {
  const lpFeeRunner = (runnerIn * p.lpFeePips) / PIPS;
  const eff = runnerIn - lpFeeRunner;
  const gross = (p.quote * eff) / (p.runner + eff);
  const projectFee = (gross * p.hookFeeBps) / 10_000n;
  const mid = Number(eff) * price(p);
  const impact = mid > 0 ? Math.max(0, Math.round((1 - Number(gross) / mid) * 10_000)) : 0;
  const lpFee = BigInt(Math.round(Number(lpFeeRunner) * price(p)));
  return { amountIn: runnerIn, amountOut: gross - projectFee, projectFee, lpFee, runnerDelta: -runnerIn, priceImpactBps: impact };
}

export function applyBuy(p: PoolState, r: SwapResult): void {
  p.quote += r.amountIn - r.projectFee;
  p.runner -= r.amountOut;
}

export function applySell(p: PoolState, r: SwapResult): void {
  p.runner += r.amountIn;
  p.quote -= r.amountOut + r.projectFee;
}

/** Protocol buyback: same pool, no project fee (mirrors the hook's buyback exemption). */
export function buybackOut(p: PoolState, quoteIn: bigint): bigint {
  const lpFee = (quoteIn * p.lpFeePips) / PIPS;
  const eff = quoteIn - lpFee;
  return (p.runner * eff) / (p.quote + eff);
}
